import { createContext, KeyboardEvent, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

const ACTIVE_TICKET_FORM_CLASS = 'ticket-form-active';

interface TicketFormContextValue {
  isTicketFormActive: boolean;
  setTicketFormActive: (active: boolean) => void;
  activeTicketFormClass: string;
  blockEnterToSend: (event: KeyboardEvent) => void;
}

export const TicketFormContext = createContext<TicketFormContextValue>({
  isTicketFormActive: false,
  setTicketFormActive: () => {},
  activeTicketFormClass: '',
  blockEnterToSend: () => {}
});

export const TicketFormContextProvider = ({ children }: { children: ReactNode }) => {
  const [isTicketFormActive, setTicketFormActive] = useState(false);

  // Block Enter-to-send while the ticket form is active (Shift+Enter still inserts a newline)
  const blockEnterToSend = useCallback(
    (event: KeyboardEvent) => {
      if (isTicketFormActive && event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isTicketFormActive]
  );

  const value = useMemo(
    () => ({
      isTicketFormActive,
      setTicketFormActive,
      activeTicketFormClass: isTicketFormActive ? ACTIVE_TICKET_FORM_CLASS : '',
      blockEnterToSend
    }),
    [isTicketFormActive, blockEnterToSend]
  );

  return <TicketFormContext.Provider value={value}>{children}</TicketFormContext.Provider>;
};

export const useTicketFormContext = () => useContext(TicketFormContext);
